import {useState} from "react"
import {Button, Tag} from "antd"
import moment from "moment-timezone"
import {useQuery} from "@tanstack/react-query"
import {handleError} from "helpers"
import {useModel} from "hooks"
import {attendanceService} from "services"
import {AttendanceChangeRequest} from "types/attendance"
import {CHANGE_REQUEST_STATUS_COLOR, ChangeRequestReviewPopup} from "./ChangeRequestReviewPopup"
import styles from "./DailyAttendanceChangeRequests.module.css"

type Props = {
    attendanceId?: number
    timezone: string
    onReviewed?: () => void
}

export function AttendanceChangeRequestList(props: Props) {
    const {attendanceId, timezone, onReviewed} = props
    const model = useModel()
    const [reviewing, setReviewing] = useState<AttendanceChangeRequest>()
    const [showHistory, setShowHistory] = useState(false)

    const {data: requests} = useQuery(
        ["attendance-change-requests", "by-attendance", attendanceId],
        async () => {
            const {data} = await attendanceService.listAttendanceChangeRequests({attendanceIds: [attendanceId]})
            return data
        },
        {
            enabled: !!attendanceId && model.isStaffOrAdmin(),
            initialData: [],
            onError: (err) => {
                handleError(err)
            }
        }
    )

    if (!requests.length) {
        return null
    }

    const [latest, ...history] = [...requests].sort((a, b) => moment(b.createdAt).diff(a.createdAt))

    const renderRequest = (request: AttendanceChangeRequest) => (
        <div key={request.id} className={styles.requestItem}>
            <span className={styles.metaLabel}>Change request</span>
            <Tag color={CHANGE_REQUEST_STATUS_COLOR[request.status]}>{request.status}</Tag>
            <span className={styles.requestedAt}>
                {moment(request.createdAt).format(model.getUserDateTimeFormat())}
            </span>
            <Button type="link" onClick={() => setReviewing(request)}>
                View request
            </Button>
        </div>
    )

    return (
        <>
            <div className={styles.requestList}>
                {renderRequest(latest)}
                {!!history.length && (
                    <>
                        <Button
                            type="link"
                            className={styles.historyToggle}
                            onClick={() => setShowHistory((value) => !value)}>
                            {showHistory ? "Hide" : "Show"} history ({history.length})
                        </Button>
                        {showHistory && history.map(renderRequest)}
                    </>
                )}
            </div>
            <ChangeRequestReviewPopup
                request={reviewing}
                timezone={timezone}
                onReviewed={onReviewed}
                onClose={() => setReviewing(undefined)}
            />
        </>
    )
}
